import React, { Suspense, useState } from "react";
import styled from "styled-components";
import { useSearchParams } from "react-router-dom";

// Non-MFC: direct library import
import { NarrativeSelector } from "@amzn/ads-forge-web-assets";

// MFC: dynamic remote imports (only work in webpack prod build, not Vite dev)
const MFCNarrativeSelector = React.lazy(
    () => import(/* @vite-ignore */ "adsForgeMfc/NarrativeSelector"),
) as React.FC<any>;

const MFCGlobalWorkbenchCartButton = React.lazy(
    () => import(/* @vite-ignore */ "adsMagnetoMfc/GlobalWorkbenchCartButton"),
) as React.FC<any>;

const MFCDynamicCartButton = React.lazy(
    () => import(/* @vite-ignore */ "adsMagnetoMfc/DynamicCartButton"),
) as React.FC<any>;

const DEFAULT_CONTENT_IDENTIFIER = "BRAND|BRAND_AID|9abe1a52-a5d2-08c9-246d-c35f436cddf8.64274031.BrandLoyalty.20260301.DE";
const CONTENT_TYPE = "INSIGHT";

const Container = styled.div`
    padding: 24px;
    font-family: sans-serif;
`;

const Toggle = styled.button<{ $active: boolean }>`
    padding: 8px 16px;
    margin-right: 8px;
    border: 2px solid ${(p) => (p.$active ? "#0073bb" : "#ccc")};
    background: ${(p) => (p.$active ? "#e6f2ff" : "#fff")};
    border-radius: 4px;
    cursor: pointer;
    font-weight: ${(p) => (p.$active ? "bold" : "normal")};
`;

const VizContainer = styled.div`
    margin-top: 16px;
    border: 1px solid #ddd;
    padding: 16px;
    min-height: 400px;
`;

const ErrorBox = styled.div`
    color: red;
    padding: 16px;
    border: 1px solid red;
    background: #fff0f0;
`;

class ErrorBoundary extends React.Component<
    { label: string; children: React.ReactNode },
    { error: Error | null }
> {
    state = { error: null as Error | null };
    static getDerivedStateFromError(error: Error) {
        return { error };
    }
    render() {
        if (this.state.error) {
            return (
                <ErrorBox>
                    <strong>[{this.props.label}] Render Error:</strong>
                    <pre>{this.state.error.message}</pre>
                    <pre>{this.state.error.stack}</pre>
                </ErrorBox>
            );
        }
        return this.props.children;
    }
}

export const CoeRepro: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Derive mode from ?mode= query param (URL is the source of truth)
    const modeParam = searchParams.get("mode");
    const mode: "mfc" | "library" = modeParam === "library" ? "library" : "mfc";

    const [contentIdentifier, setContentIdentifier] = useState(
        () => searchParams.get("contentIdentifier") || DEFAULT_CONTENT_IDENTIFIER,
    );
    const [workbenchScope, setWorkbenchScope] = useState(
        () => searchParams.get("workbenchScope") || "AccountAgnostic",
    );
    const [numPages, setNumPages] = useState(0);

    const handleModeChange = (newMode: "mfc" | "library") => {
        setSearchParams((prev) => {
            prev.set("mode", newMode);
            return prev;
        });
    };

    return (
        <Container>
            <h2>COE Reproduction: Forge MFC vs Library</h2>
            <div style={{ marginBottom: 12 }}>
                <label htmlFor="content-identifier-input">
                    <code>contentIdentifier:</code>
                </label>
                <input
                    id="content-identifier-input"
                    type="text"
                    value={contentIdentifier}
                    onChange={(e) => setContentIdentifier(e.target.value)}
                    style={{ width: "100%", padding: 8, marginTop: 4, fontFamily: "monospace" }}
                />
            </div>
            <div style={{ marginBottom: 12 }}>
                <label htmlFor="workbench-scope-input">
                    <code>workbenchScope:</code>
                </label>
                <input
                    id="workbench-scope-input"
                    type="text"
                    value={workbenchScope}
                    onChange={(e) => setWorkbenchScope(e.target.value)}
                    style={{ width: "100%", padding: 8, marginTop: 4, fontFamily: "monospace" }}
                />
            </div>
            <div>
                <Toggle $active={mode === "mfc"} onClick={() => handleModeChange("mfc")}>
                    MFC (remote CDN)
                </Toggle>
                <Toggle $active={mode === "library"} onClick={() => handleModeChange("library")}>
                    Library (bundled)
                </Toggle>
            </div>
            <VizContainer>
                {mode === "mfc" ? (
                    <ErrorBoundary label="MFC">
                        <Suspense fallback={<div>Loading MFC remote...</div>}>
                            <MFCNarrativeSelector
                                contentIdentifier={contentIdentifier}
                                contentType={CONTENT_TYPE}
                                selectedSlides={new Set([])}
                                setSelectedSlides={() => {}}
                                setNumPages={setNumPages}
                            />
                        </Suspense>
                    </ErrorBoundary>
                ) : (
                    <ErrorBoundary label="Library">
                        <NarrativeSelector
                            contentIdentifier={contentIdentifier}
                            contentType={CONTENT_TYPE}
                            selectedSlides={new Set([])}
                            setSelectedSlides={() => {}}
                            setNumPages={setNumPages}
                        />
                    </ErrorBoundary>
                )}
            </VizContainer>
            <p style={{ marginTop: 8, color: "#666" }}>
                Pages detected: {numPages} | Mode: <strong>{mode}</strong>
            </p>
            <h3 style={{ marginTop: 24 }}>Magneto MFC (adsMagnetoMfc)</h3>
            <VizContainer>
                <ErrorBoundary label="Magneto-GlobalWorkbenchCartButton">
                    <Suspense fallback={<div>Loading Magneto GlobalWorkbenchCartButton...</div>}>
                        <MFCGlobalWorkbenchCartButton
                            workbenchScope={workbenchScope}
                            clientId="InsightsHub"
                            title={'Insights Workbench'}
                        />
                    </Suspense>
                </ErrorBoundary>
                <hr style={{ margin: "16px 0" }} />
                <ErrorBoundary label="Magneto-DynamicCartButton">
                    <Suspense fallback={<div>Loading Magneto DynamicCartButton...</div>}>
                        <MFCDynamicCartButton
                            contentIdentifier={contentIdentifier}
                            contentType={CONTENT_TYPE}
                        />
                    </Suspense>
                </ErrorBoundary>
            </VizContainer>
        </Container>
    );
};
